import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { getListingBrandVariant } from "../listings/helpers/listingBrandConfig";
import { useEntitlement } from "../contexts";
import { Button, Icon, IconButton } from "../components/ui";
import "./price-review-banner.scss";

// Fired after a price or review changes from the listings page (the card's
// price check popup) — the banners re-count at once.
export const PRICE_REVIEW_CHANGED = "rc:price-review-changed";

// Cache-bust so banner art shows without a hard refresh
const ASSET_V = "20260722c";

const BANNER_SLOTS = [
  {
    variant: "rolex",
    label: "Rolex",
    image: `/assets/banners/rolex-purple.png?v=${ASSET_V}`,
  },
  {
    variant: "patek",
    label: "Patek Philippe",
    image: `/assets/banners/patek-purple.png?v=${ASSET_V}`,
  },
  {
    variant: "ap",
    label: "Audemars Piguet",
    image: `/assets/banners/audemars-purple.png?v=${ASSET_V}`,
  },
  // Every brand without a banner of its own, summed. Its name is the API's
  // 'others' key (POST /competitive-analysis/missing/others).
  {
    variant: "others",
    label: "Other brands",
    name: "others",
    image: `/assets/banners/others-purple.png?v=${ASSET_V}`,
  },
];

// The "others" slot: the counts of every brand that has no slot, added up,
// with their marketplaces merged.
const sumOtherBrands = (apiBrands) => {
  const rest = apiBrands.filter((brand) => getListingBrandVariant(brand.name) === "default");
  if (!rest.length) return null;
  const marketplaces = [];
  rest.forEach((brand) => (brand.marketplaces || []).forEach((m) => {
    const seen = marketplaces.find((x) => x.key === m.key);
    if (seen) seen.count += Number(m.count) || 0;
    else marketplaces.push({ ...m, count: Number(m.count) || 0 });
  }));
  const sum = (field) => rest.reduce((total, brand) => total + (Number(brand[field]) || 0), 0);
  return {
    name: "others",
    count: sum("count"),
    price_count: sum("price_count"),
    wf_payout_count: sum("wf_payout_count"),
    marketplaces,
  };
};

const mergeBannerSlots = (apiBrands = []) =>
  BANNER_SLOTS.map((slot) => {
    const match = slot.variant === "others"
      ? sumOtherBrands(apiBrands)
      : apiBrands.find((brand) => getListingBrandVariant(brand.name) === slot.variant);

    return {
      ...slot,
      name: match?.name || slot.name || slot.label,
      count: match?.count ?? 0,
      // Shown apart on the first line: listings out of the price range, and
      // listings whose WatchFinder payout is below the store price (one listing
      // can be in both).
      priceCount: Number(match?.price_count ?? match?.count ?? 0),
      wfPayoutCount: Number(match?.wf_payout_count ?? 0),
      // Marketplaces with at least one missing comp — the reason these listings
      // need a price review. Highlighted on the banner's first line.
      marketplaces: match?.marketplaces ?? [],
    };
  });

// One View button per reason: the price review and the WatchFinder payout.
// The open one reads Close; a lone button just reads View.
const REASONS = [
  { key: "price", label: "View prices", countKey: "priceCount" },
  { key: "wf_payout", label: "View WF payout", countKey: "wfPayoutCount" },
];

const BannerCard = ({ brand, onBrandSelect, selectedBrand, selectedReason }) => {
  const reasons = REASONS.filter((r) => brand[r.countKey] > 0);
  // No reason in the URL (an older link, or the Dashboard banner) = the first.
  const open = selectedBrand === brand.name ? (selectedReason || reasons[0]?.key || "price") : null;
  // Keep the open list's Close reachable after its last listing is fixed.
  if (open && !reasons.some((r) => r.key === open)) reasons.push({ key: open, label: "View" });
  const isExpanded = open !== null;
  const toggle = (reason) => (open === reason ? onBrandSelect(null, null) : onBrandSelect(brand.name, reason));
  const onToggle = () => (isExpanded ? onBrandSelect(null, null) : toggle(reasons[0]?.key || "price"));
  const plural = (n) => `${n} listing${n !== 1 ? "s" : ""}`;
  // Marketplace name(s) that triggered the review — "Chrono24", "eBay,
  // Chrono24". The WatchFinder payout has its own line.
  const marketplaceLabel = brand.marketplaces
    .filter((m) => m.key !== "wf_payout")
    .map((m) => m.label)
    .join(", ");

  // The whole card stays clickable for the pointer; the View / Close Button is
  // the focusable, announced control (it was a role="button" div before).
  return (
    <div
      className={`prb-card prb-card--${brand.variant}${isExpanded ? " prb-card--active" : ""}`}
      onClick={onToggle}
    >
      <div className="prb-card__content">
        <div className="prb-card__copy">
          {/* One line per reason: the price review, then the WatchFinder payout. */}
          {brand.priceCount > 0 && (
            <p className="prb-card__label">
              <strong className="prb-card__count">{plural(brand.priceCount)}</strong>
              {marketplaceLabel ? ` out of range on ${marketplaceLabel}` : " out of range"}
            </p>
          )}
          {brand.wfPayoutCount > 0 && (
            <p className="prb-card__label">
              <strong className="prb-card__count">{plural(brand.wfPayoutCount)}</strong>
              {" with low WF payout"}
            </p>
          )}
          <h3 className="prb-card__brand">{brand.label}</h3>
        </div>
        <div className="prb-card__actions">
          {reasons.map((r) => (
            <Button
              key={r.key}
              variant="secondary"
              size="sm"
              className="prb-card__view-btn"
              data-btn={`review-open-${r.key}`}
              aria-expanded={open === r.key}
              onClick={(event) => {
                event.stopPropagation();
                toggle(r.key);
              }}
            >
              {open === r.key ? "Close" : reasons.length === 1 ? "View" : r.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="prb-card__media" aria-hidden="true">
        <img
          src={brand.image}
          alt=""
          className="prb-card__graphic"
          draggable={false}
        />
      </div>
    </div>
  );
};

const PriceReviewBanner = ({ onBrandSelect, selectedBrand, selectedReason = null }) => {
  const [apiBrands, setApiBrands] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef(null);
  // Intelligence add-on (Feature 10) — see CompetitiveAnalysisBanner.
  const hasCompCheck = useEntitlement("competitive_pricing_check");

  // Re-counted on mount, after a change on this page, and when the tab comes
  // back into view (a price fixed in another tab). An empty answer clears the
  // banners — the last listing fixed must make its banner go away.
  useEffect(() => {
    if (!hasCompCheck) return undefined;
    const load = () =>
      axios
        .get("/api/listings/competitive-analysis/missing", { metadata: { silent: true } })
        .then(({ data }) => setApiBrands(Array.isArray(data?.brands) ? data.brands : []))
        .catch(() => {});
    const onVisible = () => document.visibilityState === "visible" && load();
    load();
    window.addEventListener(PRICE_REVIEW_CHANGED, load);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(PRICE_REVIEW_CHANGED, load);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [hasCompCheck]);

  // A banner with nothing to review is hidden — except the one whose review
  // list is open, so its Close button stays reachable after the last fix.
  const brands = useMemo(
    () => mergeBannerSlots(apiBrands).filter((b) => b.count > 0 || b.name === selectedBrand),
    [apiBrands, selectedBrand]
  );
  const isFirst = activeIndex <= 0;
  const isLast = activeIndex >= brands.length - 1;

  const syncActiveFromScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track || !track.clientWidth) return;

    const index = Math.round(track.scrollLeft / track.clientWidth);
    setActiveIndex(Math.max(0, Math.min(index, brands.length - 1)));
  }, [brands.length]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(syncActiveFromScroll);
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      track.removeEventListener("scroll", onScroll);
    };
  }, [syncActiveFromScroll]);

  const scrollToIndex = (index) => {
    const track = trackRef.current;
    if (!track) return;

    const next = Math.max(0, Math.min(index, brands.length - 1));
    track.scrollTo({
      left: next * track.clientWidth,
      behavior: "smooth",
    });
    setActiveIndex(next);
  };

  const goPrev = (event) => {
    event.stopPropagation();
    if (isFirst) return;
    scrollToIndex(activeIndex - 1);
  };

  const goNext = (event) => {
    event.stopPropagation();
    if (isLast) return;
    scrollToIndex(activeIndex + 1);
  };

  // Intelligence add-on (Feature 10): the banner's static brand slots would
  // otherwise render even with no API data, so guard the whole component.
  if (!hasCompCheck || brands.length === 0) return null;

  return (
    <div className="prb" role="region" aria-label="Price review by brand">
      <div className="prb-row" ref={trackRef}>
        {brands.map((brand) => (
          <div key={brand.variant} className="prb-slide">
            <BannerCard
              brand={brand}
              onBrandSelect={onBrandSelect}
              selectedBrand={selectedBrand}
              selectedReason={selectedReason}
            />
          </div>
        ))}
      </div>

      {!isFirst && (
        <IconButton
          round
          className="prb-nav prb-nav--prev"
          label="Previous brand banner"
          data-btn="review-prev"
          onClick={goPrev}
        >
          <Icon name="ChevronLeft" size="sm" />
        </IconButton>
      )}

      {!isLast && (
        <IconButton
          round
          className="prb-nav prb-nav--next"
          label="Next brand banner"
          data-btn="review-next"
          onClick={goNext}
        >
          <Icon name="ChevronRight" size="sm" />
        </IconButton>
      )}
    </div>
  );
};

export default PriceReviewBanner;
